import { Box, TextField, useTheme } from '@mui/material'
import React, { useState } from 'react'
import GenericButton from '../../../components/GenericButton'
import { ButtonType } from '../../../components/GenericButton/styles'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { setSectionName, type ISurveySection } from '../../../store/slices/SurveyTemplateSlice'
import { useAppDispatch } from '../../../store'
import { SectionNameText } from './styles'
import { notification } from '../../../components/Notifications'

interface SectionNameProps {
  section: ISurveySection
  surveySectionsList: ISurveySection[]
}

// Helper function to determine the correct name to edit
const getSectionEditName = (section: ISurveySection): string => {
  if (section.name.startsWith('Untitled section')) {
    const trimmedTitle = (section.title ?? '').trim()
    if (!trimmedTitle) {
      return section.name
    }
    return trimmedTitle
  }
  return section.name
}

const normalizeSectionName = (value: string): string => value.toLocaleLowerCase()

const SectionName: React.FC<SectionNameProps> = ({
  section,
  surveySectionsList
}) => {
  const [editableSectionId, setEditableSectionId] = useState<string | null>(null)
  const [name, setName] = useState<string>('')

  const theme = useTheme()
  const dispatch = useAppDispatch()

  const handleCloseSectionName = (): void => {
    setEditableSectionId(null)
  }

  const checkUniqueSectionNames = (nextName: string): boolean => {
    const normalizedNextName = normalizeSectionName(nextName)
    return !surveySectionsList.some((existingSection: ISurveySection) =>
      existingSection.surveySectionId !== section.surveySectionId &&
      normalizeSectionName(existingSection.name) === normalizedNextName
    )
  }

  const handleSectionNameChange = (): void => {
    const nextName = name || 'Untitled section'
    if (checkUniqueSectionNames(nextName)) {
      dispatch(setSectionName({ surveySectionId: section.surveySectionId, value: nextName }))
      setEditableSectionId(null)
    } else {
      notification('Section name should be Unique', { variant: 'error' })
    }
  }

  const handleEditSectionName = (surveySectionId: string, currentName: string): void => {
    setEditableSectionId(surveySectionId)
    setName(currentName)
  }

  return (
    <>
      {
        editableSectionId === section.surveySectionId

          ? <Box display="flex" gap={2} alignItems="center">
            <TextField
              sx={{ marginTop: theme.spacing(2.5), minWidth: '300px' }}
              placeholder="Untitled section"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
              }}
            />
            <Box display="flex" gap={1} alignItems="center" mt={2.5}>
              <GenericButton
                onClick={handleSectionNameChange}
                buttonType={ButtonType.Secondary}
                style={{ padding: '10px' }}
                icon={<CheckRoundedIcon />}
                disabled={section.name === name}
              />
              <GenericButton
                onClick={handleCloseSectionName}
                buttonType={ButtonType.Secondary}
                style={{ padding: '10px' }}
                icon={<CloseRoundedIcon />}
              />
            </Box>
          </Box>
          : <SectionNameText onClick={() => { handleEditSectionName(section.surveySectionId, getSectionEditName(section)) }}>
            {section.name}
          </SectionNameText>
      }
    </>
  )
}

export default SectionName
